import { useRef, useEffect, memo, useState } from 'react';

import * as pdfjs from 'pdfjs-dist';
import type { PDFPageProxy } from 'pdfjs-dist';

import Box from '@mui/material/Box';
import { css } from '@emotion/react';

interface Props {
  readonly index: number;
  readonly page: PDFPageProxy | undefined;
  readonly scale: number;
}

// MHO : from https://codesandbox.io/s/pdf-viewer-w7wpu
const PdfPageCmp = ({ index, page, scale }: Readonly<Props>) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const textLayerRef = useRef<HTMLDivElement | null>(null);

  const [viewPortScale, setViewPortScale] = useState(1);

  const [isRendered, setIsRendered] = useState(false);

  useEffect(() => {
    if (!page) {
      return;
    }

    const viewport = page.getViewport({ scale });
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setViewPortScale(viewport.scale);

    // Prepare canvas using PDF page dimensions
    const canvas = canvasRef.current;
    if (canvas) {
      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      if (context) {
        // Render PDF page into canvas context
        const renderContext = {
          canvasContext: context,
          viewport,
        };

        const renderTask = page.render(renderContext);
        renderTask.promise
          .then(() => {
            setIsRendered(true);
          })
          .catch((error: unknown) => {
            throw error;
          });
      }

      page
        .getTextContent()
        .then((textContentSource) => {
          if (!textLayerRef.current) {
            return;
          }

          void new pdfjs.TextLayer({
            textContentSource,
            viewport,
            container: textLayerRef.current,
          }).render();
        })
        .catch((error: unknown) => {
          throw error;
        });
    }
  }, [page, scale]);

  return (
    <Box
      id={`pdf-viewer-page-${(index + 1).toFixed()}-${isRendered ? 'rendered' : 'loading'}`}
      sx={{
        position: 'relative',
        maxWidth: '100%',
        overflow: 'auto',
      }}
    >
      <canvas ref={canvasRef} />
      <Box
        css={css`
          --scale-factor: ${viewPortScale};

          & > span {
            color: transparent;
            position: absolute;
            white-space: pre;
            cursor: text;
            transform-origin: 0% 0%;
          }
        `}
        ref={textLayerRef}
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          right: 0,
          bottom: 0,
          overflow: 'hidden',
          opacity: 0.2,
          lineHeight: 1,
        }}
      />
    </Box>
  );
};

export const PdfPage = memo(PdfPageCmp);

PdfPage.displayName = 'PdfPage';
